import React from 'react';
import { componentConfidenceBand, relationshipConfidenceBand, truncateLabel } from '../appHelpers';

function edgeGeometry(source, target, index) {
  const startX = source.x + 98;
  const startY = source.y + 28;
  const endX = target.x - 98;
  const endY = target.y + 28;
  const deltaX = endX - startX;
  const deltaY = endY - startY;
  const laneOffset = ((index % 3) - 1) * 14;
  const horizontalPull = Math.max(48, Math.abs(deltaX) * 0.36);
  const verticalPull = deltaY * 0.22 + laneOffset;
  const control1X = startX + horizontalPull;
  const control1Y = startY + verticalPull;
  const control2X = endX - horizontalPull;
  const control2Y = endY - verticalPull;

  return {
    path: `M ${startX} ${startY} C ${control1X} ${control1Y}, ${control2X} ${control2Y}, ${endX} ${endY}`,
    labelX: startX + deltaX * 0.5,
    labelY: startY + deltaY * 0.5 + laneOffset - 12,
  };
}

function nodePosition(index, total, width, height) {
  const columns = Math.max(1, Math.min(3, total));
  const column = index % columns;
  const row = Math.floor(index / columns);
  const rows = Math.max(1, Math.ceil(total / columns));
  const horizontalGap = 248;
  const verticalGap = 168;
  const gridWidth = (columns - 1) * horizontalGap;
  const gridHeight = (rows - 1) * verticalGap;
  const startX = width / 2 - gridWidth / 2;
  const startY = height / 2 - gridHeight / 2;
  return {
    x: startX + column * horizontalGap,
    y: startY + row * verticalGap,
  };
}

function reliabilityIssueTitle(issue) {
  if (!issue) {
    return null;
  }

  const severityLabel = issue.highestSeverity === 'critical' ? 'critical' : issue.highestSeverity;
  return `${issue.count} active reliability finding${issue.count === 1 ? '' : 's'} · highest severity ${severityLabel}`;
}

export default function ArchitectureGraph({ architecture, highlightedComponentId, componentReliabilityIssues = new Map() }) {
  const showEdgeLabels = architecture.relationships.length <= 5;
  const width = Math.max(900, Math.min(1240, 328 * Math.max(1, Math.min(3, architecture.components.length))));
  const rows = Math.max(1, Math.ceil(architecture.components.length / Math.max(1, Math.min(3, architecture.components.length))));
  const height = Math.max(360, rows * 196);
  const positions = new Map(
    architecture.components.map((component, index) => [component.id, nodePosition(index, architecture.components.length, width, height)])
  );

  return (
    <div className="architecture-shell">
      <svg className="architecture-svg" viewBox={`0 0 ${width} ${height}`} role="img" aria-label="System architecture">
        <defs>
          <marker id="arrowhead" markerWidth="7" markerHeight="7" refX="5.8" refY="3.5" orient="auto">
            <path d="M 0 0 L 7 3.5 L 0 7 z" className="graph-arrow" />
          </marker>
        </defs>
        {architecture.relationships.map((relationship, index) => {
          const source = positions.get(relationship.source_component_id);
          const target = positions.get(relationship.target_component_id);
          if (!source || !target) {
            return null;
          }
          const edgeBand = relationshipConfidenceBand(relationship);
          const geometry = edgeGeometry(source, target, index);
          return (
            <g key={relationship.id}>
              <path
                className={`graph-edge graph-edge-${edgeBand}`}
                d={geometry.path}
                markerEnd="url(#arrowhead)"
                data-confidence-band={edgeBand}
              />
              {showEdgeLabels ? (
                <text className="graph-edge-label" x={geometry.labelX} y={geometry.labelY}>
                  {relationship.relationship_type}
                </text>
              ) : null}
            </g>
          );
        })}
        {architecture.components.map((component) => {
          const position = positions.get(component.id);
          const highlighted = highlightedComponentId === component.id;
          const fullLabel = component.display_name || component.name;
          const compactLabel = truncateLabel(fullLabel, component.inferred ? 18 : 20);
          const confidenceBand = componentConfidenceBand(component);
          const reliabilityIssue = componentReliabilityIssues.get(component.id) || null;
          const nodeClassName = [
            'graph-node',
            component.inferred ? `graph-node-inferred graph-node-${confidenceBand}` : 'graph-node-declared',
            reliabilityIssue ? `graph-node-issue graph-node-issue-${reliabilityIssue.band}` : '',
            highlighted ? 'graph-node-highlight' : '',
          ]
            .filter(Boolean)
            .join(' ');
          const title = reliabilityIssue ? `${fullLabel} — ${reliabilityIssueTitle(reliabilityIssue)}` : fullLabel;
          return (
            <g
              key={component.id}
              transform={`translate(${position.x - 98}, ${position.y - 40})`}
              data-confidence-band={confidenceBand}
              data-reliability-band={reliabilityIssue?.band || 'none'}
            >
              <title>{title}</title>
              <rect className={nodeClassName} width="196" height="80" rx="20" />
              <text className="graph-node-kicker" x="16" y="22">
                {component.component_type}
              </text>
              <text className="graph-node-title" x="16" y="44">
                {compactLabel}
              </text>
              <text className="graph-node-status" x="16" y="61">
                {component.status}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
